'use client';

import { useEffect, useRef, useState } from 'react';
import { useCurrencyContext } from '@contexts/currency-context';
import { useBetterMediaQuery } from '@hooks/use-ssr-media-query';
import { LeagueEvent } from '@interfaces/APITravel';
import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useBuildPackagesStore } from '@store/build-package-store';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import {
  Divider,
  Drawer,
  DrawerContent,
  DrawerTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@v2/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTicketsStore, type TicketSortBy } from 'src/stores/tickets-store';
import { useTicketPriceOffset } from '../utils/use-ticket-price-offset';
import { usePackagesParams } from '@hooks/use-packages-params';
import { getTicketQuotePrice } from '../utils/ticket-quote-price';
import type { SelectedTicketQuote } from '../utils/use-selected-ticket-quote';
import { ArenaView } from './ArenaView';
import { ConfirmSelection } from './ConfirmSelection';
import { MobileTicketFilters } from './MobileTicketFilters';
import { SeatPinPreview } from './SeatPinPreview';
import {
  TicketQuantityDialog,
  TicketQuantityDrawer,
} from './TicketQuantityModal';
import { TicketList } from './TicketList';

type Props = {
  event: LeagueEvent;
  ticketsList: TicketGroup[];
  loading: boolean;
  selectedQuote: SelectedTicketQuote;
  retryQuote: () => void;
};

export const Sidebar = function Sidebar({
  ticketsList,
  loading,
  event,
  selectedQuote,
  retryQuote,
}: Props) {
  const isMobile = useBetterMediaQuery('(max-width: 1024px)');
  const reduceMotion = useReducedMotion();
  const ticketSelected = useTicketsStore((state) => state.ticketSelected);
  const ticketsQuantity = useTicketsStore((state) => state.ticketsQuantity);
  const isTicketSelectionConfirmed = useTicketsStore(
    (state) => state.isTicketSelectionConfirmed
  );
  const isBundle = useBuildPackagesStore((state) => state.isBundle);
  const { priceRate, currency, priceRateLoading } = useCurrencyContext();
  const priceOffset = useTicketPriceOffset(ticketsList);
  const { packageType } = usePackagesParams();
  const isTicketsOnly = packageType === 'tickets';
  const quoteReady = selectedQuote.status === 'ready' && !priceRateLoading;
  const priceLabel =
    isTicketsOnly && ticketSelected
      ? quoteReady
        ? getTicketQuotePrice(
            ticketSelected.retail_price,
            ticketsQuantity,
            selectedQuote.quote,
            priceRate,
            currency
          ).display.perPerson
        : selectedQuote.status === 'error'
          ? 'Price unavailable'
          : 'Calculating price…'
      : ticketSelected
        ? `${isBundle ? '+ ' : ''}${formatCurrencyV2(
            Math.max(0, ticketSelected.retail_price - priceOffset),
            priceRate,
            currency,
            false,
            false,
            true,
            true
          )}`
        : '';

  const [shouldAutoOpenQuantity, setShouldAutoOpenQuantity] = useState(false);
  const hasAutoOpenedQuantityRef = useRef(false);

  useEffect(() => {
    if (hasAutoOpenedQuantityRef.current) return;
    if (isMobile == null) return;
    if (ticketSelected) return;

    hasAutoOpenedQuantityRef.current = true;
    setShouldAutoOpenQuantity(true);
  }, [isMobile, ticketSelected]);

  useEffect(() => {
    if (!shouldAutoOpenQuantity) return;
    setShouldAutoOpenQuantity(false);
  }, [shouldAutoOpenQuantity]);

  if (isMobile) {
    return (
      <div className="bg-white">
        <div className="bg-white">
          <MainContent
            ticketsList={ticketsList}
            loading={loading}
            event={event}
            isMobile
            shouldAutoOpen={shouldAutoOpenQuantity}
          />
        </div>

        <Drawer
          handleOnly
          open={Boolean(ticketSelected)}
          onOpenChange={(open) => {
            if (!open) clearTicketSelection();
          }}
          shouldScaleBackground={false}
        >
          <DrawerContent
            aria-describedby={undefined}
            className="mx-auto max-h-[min(82dvh,760px)] w-full overflow-hidden rounded-t-[24px] border-0 bg-white shadow-shadowLg [&>div:first-child]:mt-3 [&>div:first-child]:h-1 [&>div:first-child]:w-10 [&>div:first-child]:bg-gray-300"
          >
            <DrawerTitle className="sr-only">Ticket details</DrawerTitle>
            <div
              key={ticketSelected?.id ?? 'empty'}
              data-vaul-no-drag
              className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
            >
              {ticketSelected ? (
                <ConfirmSelection
                  ticket={ticketSelected}
                  ticketsQuantity={ticketsQuantity}
                  priceLabel={priceLabel}
                  isPricePending={isTicketsOnly && !quoteReady}
                  onRetryPrice={
                    isTicketsOnly && selectedQuote.status === 'error'
                      ? retryQuote
                      : undefined
                  }
                  isSubmitting={isTicketSelectionConfirmed}
                  onBack={clearTicketSelection}
                  seatMap={
                    event.venue?.id != null &&
                    event.configuration?.id != null ? (
                      <SeatPinPreview
                        venueId={String(event.venue.id)}
                        configurationId={String(event.configuration.id)}
                        sectionName={
                          ticketSelected.tevo_section_name ||
                          ticketSelected.section
                        }
                        sectionLabel={ticketSelected.section}
                        className="mt-6 aspect-[2/1] w-full"
                      />
                    ) : undefined
                  }
                />
              ) : null}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-hidden bg-white">
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={ticketSelected ? 'details' : 'list'}
          initial={{
            opacity: 0,
            x: reduceMotion ? 0 : ticketSelected ? 20 : -20,
          }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: reduceMotion ? 0 : ticketSelected ? 20 : -20 }}
          transition={{ duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' }}
          className="flex h-full flex-col bg-white"
        >
          {ticketSelected ? (
            <ConfirmSelection
              ticket={ticketSelected}
              ticketsQuantity={ticketsQuantity}
              priceLabel={priceLabel}
              isPricePending={isTicketsOnly && !quoteReady}
              onRetryPrice={
                isTicketsOnly && selectedQuote.status === 'error'
                  ? retryQuote
                  : undefined
              }
              isSubmitting={isTicketSelectionConfirmed}
              onBack={clearTicketSelection}
            />
          ) : (
            <>
              <div className="px-6 py-8">
                <div className="mb-6">
                  <TicketQuantityDialog defaultOpen={shouldAutoOpenQuantity} />
                </div>
                <Divider />
              </div>

              <MainContent
                ticketsList={ticketsList}
                loading={loading}
                event={event}
                isMobile={false}
                shouldAutoOpen={shouldAutoOpenQuantity}
              />
            </>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

function clearTicketSelection() {
  useTicketsStore.setState({
    ticketSelected: null,
    isTicketSelectionConfirmed: false,
    taxQuota: { tax: 0, tax_signature: null },
  });
}

function MainContent({
  loading,
  ticketsList,
  event,
  isMobile,
  shouldAutoOpen = false,
}: {
  ticketsList: TicketGroup[];
  loading: boolean;
  event: LeagueEvent;
  isMobile: boolean;
  shouldAutoOpen?: boolean;
}) {
  const tab = useTicketsStore((state) => state.sortby);
  const setSortBy = useTicketsStore((state) => state.setSortBy);

  if (isMobile) {
    return (
      <div className="bg-gray-50">
        <div className="flex items-center gap-x-2 px-4 pb-2">
          <MobileTicketFilters ticketsList={ticketsList} />
          <TicketQuantityDrawer defaultOpen={shouldAutoOpen} />
        </div>

        <div className="mb-3 h-[200px] w-full bg-white">
          <ArenaView event={event} ticketGroups={ticketsList} />
        </div>
        <TicketList
          event={event}
          list={ticketsList}
          loading={loading}
          showFilters={false}
        />
      </div>
    );
  }

  return (
    <Tabs
      value={tab}
      onValueChange={(type: string) => {
        setSortBy(type as TicketSortBy);
      }}
      className="bg-white lg:flex lg:min-h-0 lg:flex-1 lg:flex-col"
    >
      <TabsList variant="outline" className="grid w-full grid-cols-3">
        <TabsTrigger
          variant="outline"
          value="lowestPrice"
          className="relative z-10 mb-[-1px] w-full pb-4 pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:border-primary-500 sm:text-sm"
        >
          Lowest price
        </TabsTrigger>
        <TabsTrigger
          variant="outline"
          value="bestValue"
          className="relative z-10 mb-[-1px] w-full pb-4 pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:border-primary-500 sm:text-sm"
        >
          Best value
        </TabsTrigger>
        <TabsTrigger
          variant="outline"
          value="bestLocation"
          className="relative z-10 mb-[-1px] w-full pb-4 pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:border-primary-500 sm:text-sm"
        >
          Best location
        </TabsTrigger>
      </TabsList>
      <div className="mt-0 bg-gray-50 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
        <TicketList event={event} list={ticketsList} loading={loading} />
      </div>
    </Tabs>
  );
}
